import { isCorrect } from "@aybuben/core/exercises/meaning/logic.ts";
import type { MeaningStep } from "@aybuben/core/lesson.ts";
import { useEffect } from "react";

import { useCourse } from "../../content/context.tsx";
import { useStrings } from "../../i18n/index.tsx";
import { COL } from "../../theme.ts";
import { FitG, G, Stage } from "../../ui/primitives.tsx";
import { SoundButton, useSound } from "../../ui/sound.tsx";
import { SpeakerRow } from "../../ui/SpeakerRow.tsx";
import { OptionGrid, StepHead } from "../kit.tsx";
import { registerView, type ExerciseViewProps, type FeedbackProps } from "../view-registry.ts";

export function MeaningView({
	step,
	answer,
	setAnswer,
	status,
	retry,
}: ExerciseViewProps<MeaningStep, string>) {
	const t = useStrings();
	const course = useCourse();
	const sound = useSound();
	const audio = course.itemById(step.word.id).audio;
	// Heard on arrival in hear mode; in read mode the sound would give the word away.
	useEffect(() => {
		if (step.mode === "hear" && audio) sound.playAuto(audio);
	}, [step.mode, audio, sound]);
	return (
		<div>
			<StepHead
				title={t(step.mode === "hear" ? "lesson.meaningHear" : "lesson.meaningRead")}
				retry={retry}
				retryNote={t("lesson.retryNote")}
			/>
			<div className="mt-5">
				{step.mode === "hear" && audio ? (
					<SpeakerRow asset={audio} status={status} />
				) : (
					<Stage>
						<FitG size={54}>{step.word.a}</FitG>
					</Stage>
				)}
			</div>
			<OptionGrid
				options={step.options}
				isRight={(id) => isCorrect(step, id)}
				sel={answer}
				status={status}
				onSel={setAnswer}
			/>
		</div>
	);
}

export function MeaningFeedback({ step }: FeedbackProps<MeaningStep>) {
	const course = useCourse();
	const sound = useSound();
	const audio = course.itemById(step.word.id).audio;
	// Spoken once checked, not before: before, it would name the right tile.
	useEffect(() => {
		if (audio) sound.playAuto(audio);
	}, [audio, sound]);
	return (
		<div className="mt-2 flex items-center gap-3">
			<G size={30}>{step.word.a}</G>
			<div className="text-sm leading-snug" style={{ color: COL.ink }}>
				{step.word.m}
			</div>
			{audio && <SoundButton asset={audio} className="ml-auto" />}
		</div>
	);
}

registerView<MeaningStep, string>("meaning", {
	View: MeaningView,
	Feedback: MeaningFeedback,
	action: "check",
});
